import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { HCLayout } from "@/components/hc/HCLayout";
import { GateCard } from "@/components/hc/GateCard";
import { ReceiptList } from "@/components/hc/ReceiptList";

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
    "Review proposed",
    "Control gates (frozen)",
    "Role contributions",
    "Shared decision",
    "Coordinated action",
];

const STATE_STEP = {
    FROZEN: 1,
    READY_FOR_DECISION: 3,
    RELEASED: 4,
    DEFERRED: 4,
    DECLINED: 4,
    ESCALATED: 4,
};

const STATE_LABEL = {
    FROZEN: { text: "FROZEN — awaiting the four control gates", color: "var(--amber)" },
    READY_FOR_DECISION: { text: "GATES SATISFIED — ready for a shared decision", color: "var(--forest)" },
    RELEASED: { text: "RELEASED — coordinated action under way", color: "var(--forest)" },
    DEFERRED: { text: "DEFERRED — plan remains under review", color: "var(--ink-soft)" },
    DECLINED: { text: "DECLINED — current arrangements stand", color: "var(--crimson)" },
    ESCALATED: { text: "ESCALATED — with the multidisciplinary team", color: "var(--amber)" },
};

export default function MLTC() {
    const [scenario, setScenario] = useState(null);
    const [journey, setJourney] = useState(null);
    const [receipts, setReceipts] = useState([]);
    const [conditions, setConditions] = useState("");
    const [decisionError, setDecisionError] = useState(null);
    const [busy, setBusy] = useState(false);

    const loadReceipts = useCallback(async (jid) => {
        const r = await axios.get(`${API}/api/mltc/journeys/${jid}/receipts`);
        setReceipts(r.data.receipts);
    }, []);

    useEffect(() => {
        (async () => {
            const s = await axios.get(`${API}/api/mltc/scenario`);
            setScenario(s.data);
            const saved = sessionStorage.getItem("mltc_journey_id");
            if (saved) {
                try {
                    const j = await axios.get(`${API}/api/mltc/journeys/${saved}`);
                    setJourney(j.data);
                    await loadReceipts(saved);
                    return;
                } catch {
                    sessionStorage.removeItem("mltc_journey_id");
                }
            }
            const j = await axios.post(`${API}/api/mltc/journeys`);
            sessionStorage.setItem("mltc_journey_id", j.data.journey_id);
            setJourney(j.data);
            await loadReceipts(j.data.journey_id);
        })();
    }, [loadReceipts]);

    const restart = async () => {
        sessionStorage.removeItem("mltc_journey_id");
        setReceipts([]);
        setConditions("");
        setDecisionError(null);
        const j = await axios.post(`${API}/api/mltc/journeys`);
        sessionStorage.setItem("mltc_journey_id", j.data.journey_id);
        setJourney(j.data);
        await loadReceipts(j.data.journey_id);
    };

    const respond = async (gateId, partId, values) => {
        const r = await axios.post(
            `${API}/api/mltc/journeys/${journey.journey_id}/gates/${gateId}/parts/${partId}/respond`,
            { responses: values }
        );
        setJourney(r.data.journey);
        await loadReceipts(journey.journey_id);
    };

    const decide = async (choice) => {
        setBusy(true);
        setDecisionError(null);
        try {
            const r = await axios.post(
                `${API}/api/mltc/journeys/${journey.journey_id}/decision`,
                { choice, conditions: conditions || null }
            );
            setJourney(r.data.journey);
            await loadReceipts(journey.journey_id);
        } catch (e) {
            setDecisionError(e?.response?.data?.detail || "Decision could not be recorded.");
        }
        setBusy(false);
    };

    if (!scenario || !journey) {
        return (
            <HCLayout>
                <p className="mono-label" data-testid="mltc-loading">Preparing the governed journey…</p>
            </HCLayout>
        );
    }

    const person = scenario.scenario.person;
    const gatesSatisfied = Object.values(journey.gates).every((g) => g.status === "SATISFIED");
    const decided = !!journey.decision;
    const stateInfo = STATE_LABEL[journey.state];
    const activeStep = decided || gatesSatisfied ? STATE_STEP[journey.state] : receipts.some((r) => r.type === "RESPONSE" || r.type === "ACCEPTANCE") ? 2 : 1;

    return (
        <HCLayout>
            {/* Hero */}
            <section className="border-b hairline pb-8" data-testid="mltc-hero">
                <p className="mono-label">MLTC Models of Care demonstrator · synthetic scenario</p>
                <h1
                    className="font-serif mt-3 max-w-4xl"
                    style={{ fontSize: "clamp(30px, 4.5vw, 52px)", lineHeight: 1.05, letterSpacing: "-0.01em" }}
                >
                    How does a shared decision made with the patient become coordinated,
                    accountable action across the wider model of care?
                </h1>
                <p className="mt-4 max-w-3xl text-base leading-relaxed" style={{ color: "var(--ink-soft)" }}>
                    {scenario.scenario.review_question}
                </p>
                <div
                    className="panel-alt mt-5 max-w-3xl px-4 py-3"
                    data-testid="hypothesis-disclaimer"
                >
                    <p className="text-sm leading-relaxed" style={{ color: "var(--ink-soft)" }}>
                        This synthetic journey is a configurable model-of-care hypothesis for
                        clinical, public and operational review. It does not recommend treatment
                        or represent a validated care pathway.
                    </p>
                </div>
                <div className="mt-5 flex flex-wrap items-center gap-4">
                    <span
                        className="mono-label border px-3 py-1.5"
                        style={{ color: stateInfo.color, borderColor: stateInfo.color }}
                        data-testid="journey-state-badge"
                    >
                        {stateInfo.text}
                    </span>
                    <button className="btn-ghost" onClick={restart} data-testid="restart-journey-btn" style={{ padding: "8px 16px", fontSize: 13 }}>
                        Restart journey
                    </button>
                    <span className="mono-data" style={{ fontSize: 11, color: "var(--ink-muted)" }}>
                        {journey.journey_id}
                    </span>
                </div>
            </section>

            {/* Journey strip */}
            <section className="panel-alt mt-8 px-5 py-5 overflow-x-auto" data-testid="journey-strip">
                <p className="mono-label mb-4">The governed journey</p>
                <ol className="flex min-w-max items-center gap-4">
                    {STEPS.map((s, i) => (
                        <li key={s} className="flex items-center gap-4">
                            <div className="flex items-center gap-2.5">
                                <span
                                    className="inline-flex h-7 w-7 items-center justify-center border font-mono"
                                    style={{
                                        fontSize: 11,
                                        fontFamily: "JetBrains Mono, monospace",
                                        borderColor: "var(--ink)",
                                        background: i <= activeStep ? "var(--ink)" : "transparent",
                                        color: i <= activeStep ? "#fff" : "var(--ink)",
                                    }}
                                >
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                                <span
                                    className="font-serif whitespace-nowrap"
                                    style={{ fontSize: 15, color: i <= activeStep ? "var(--ink)" : "var(--ink-muted)" }}
                                >
                                    {s}
                                </span>
                            </div>
                            {i < STEPS.length - 1 && (
                                <span aria-hidden style={{ width: 44, height: 1, background: "var(--rule)" }} />
                            )}
                        </li>
                    ))}
                </ol>
            </section>

            {/* Person panel */}
            <section className="mt-8 grid gap-6 lg:grid-cols-3" data-testid="person-panel">
                <div className="panel px-6 py-6 lg:col-span-2">
                    <p className="mono-label">The person at the centre</p>
                    <h2 className="font-serif mt-2" style={{ fontSize: 26 }}>
                        {person.name}, {person.age}
                    </h2>
                    <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--ink-soft)" }}>
                        {person.context}
                    </p>
                    <div className="mt-4 grid gap-x-8 gap-y-2 sm:grid-cols-2">
                        {[
                            ["Conditions", person.conditions.join(" · ")],
                            ["Synthetic ref", person.identifiers.synthetic_ref],
                            ["Practice", person.identifiers.practice],
                            ["Pharmacy", person.identifiers.pharmacy],
                            ["Neighbourhood service", person.identifiers.neighbourhood_service],
                        ].map(([k, v]) => (
                            <div key={k} className="border-b hairline py-2">
                                <p className="mono-label" style={{ fontSize: 10 }}>{k}</p>
                                <p className="text-sm" style={{ color: "var(--ink)" }}>{v}</p>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="panel-alt px-6 py-6 flex flex-col justify-center">
                    <p className="mono-label">Elsie's stated priority</p>
                    <blockquote className="font-serif mt-3" style={{ fontSize: 20, lineHeight: 1.4, color: "var(--ink)" }}>
                        “{person.priority}”
                    </blockquote>
                </div>
            </section>

            {/* Gates */}
            <section className="mt-10" data-testid="gates-section">
                <div className="flex items-end justify-between gap-4 border-b hairline pb-3">
                    <div>
                        <p className="mono-label">The control envelope</p>
                        <h2 className="font-serif mt-1" style={{ fontSize: 26 }}>
                            Four gates hold the proposed change frozen
                        </h2>
                    </div>
                </div>
                <div className="mt-6 grid gap-6 lg:grid-cols-2">
                    {scenario.gates.map((g, i) => (
                        <GateCard
                            key={g.id}
                            gate={g}
                            index={i}
                            state={journey.gates[g.id]}
                            onRespond={respond}
                            disabled={decided}
                        />
                    ))}
                </div>
            </section>

            {/* Decision */}
            <section className="panel mt-10 px-6 py-6" data-testid="decision-section">
                <p className="mono-label">The shared clinical decision</p>
                <h2 className="font-serif mt-1" style={{ fontSize: 26 }}>
                    Decide with Elsie
                </h2>
                <p className="mt-2 max-w-3xl text-sm leading-relaxed" style={{ color: "var(--ink-soft)" }}>
                    Agreement requires all four gates to be satisfied. Defer, decline or escalate are
                    available at any point. RELEASED is the workflow state that follows an agreed plan —
                    not a clinical choice.
                </p>
                {decided ? (
                    <div className="panel-alt mt-5 px-5 py-4" data-testid="decision-result">
                        <p className="mono-label" style={{ color: stateInfo.color }}>{journey.state}</p>
                        <p className="font-serif mt-1" style={{ fontSize: 20 }}>
                            {journey.decision.label}
                            {journey.decision.conditions ? ` — ${journey.decision.conditions}` : ""}
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="mt-5">
                            <label className="mono-label" style={{ fontSize: 10 }}>
                                Review conditions (required for “agree with review conditions”, max 240 chars)
                            </label>
                            <textarea
                                data-testid="decision-conditions-input"
                                className="mt-1"
                                rows={2}
                                maxLength={240}
                                value={conditions}
                                onChange={(e) => setConditions(e.target.value)}
                                placeholder="e.g. Pharmacist technique review at 2 weeks; revert if breathlessness worsens"
                            />
                        </div>
                        {decisionError && (
                            <p className="mt-2 text-sm" style={{ color: "var(--crimson)" }} data-testid="decision-error">
                                {decisionError}
                            </p>
                        )}
                        <div className="mt-4 flex flex-wrap gap-3">
                            {scenario.decisions.map((d) => (
                                <button
                                    key={d.id}
                                    data-testid={`decision-${d.id}-btn`}
                                    className={d.requires_gates ? "btn-accent" : "btn-ghost"}
                                    disabled={busy || (d.requires_gates && !gatesSatisfied)}
                                    onClick={() => decide(d.id)}
                                    title={d.requires_gates && !gatesSatisfied ? "All four gates must be satisfied first" : ""}
                                >
                                    {d.label}
                                </button>
                            ))}
                        </div>
                    </>
                )}
            </section>

            {/* Receipts */}
            <div className="mt-10">
                <ReceiptList receipts={receipts} />
            </div>
        </HCLayout>
    );
}
